import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Dashboard from "./Dashboard";
import LibraryPage from "./LibraryPage";
import NotificationsPage from "./NotificationsPage";
import ProfilePage from "./ProfilePage";
import GeneralRulesPage from "./GeneralRulesPage";
import AppNav from "./AppNav";
import { flushPendingBookListUpdates } from "../utils/bookListCache";

const SHELL_PAGES = [
  { path: "/dashboard", Component: Dashboard },
  { path: "/library", Component: LibraryPage },
  { path: "/notifications", Component: NotificationsPage },
  { path: "/profile", Component: ProfilePage },
  { path: "/profile/general-rules", Component: GeneralRulesPage },
];

function AppShell() {
  const location = useLocation();
  const activePath =
    location.pathname.startsWith("/profile/")
      ? "/profile/general-rules"
      : SHELL_PAGES.find((p) => location.pathname === p.path)?.path ?? "/dashboard";

  // All pages mount immediately so their data fetches run in parallel on app start
  const scrollPositions = useRef({});
  const prevPathRef = useRef(activePath);

  useEffect(() => {
    const prev = prevPathRef.current;

    if (prev) {
      scrollPositions.current[prev] = window.scrollY;
    }

    const saved = scrollPositions.current[activePath] ?? 0;
    window.scrollTo(0, saved);

    prevPathRef.current = activePath;
  }, [activePath]);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const flushPending = () => {
      flushPendingBookListUpdates(apiUrl).then(() => {
        window.dispatchEvent(new Event("book-lists-updated"));
      });
    };

    flushPending();
    window.addEventListener("online", flushPending);
    window.addEventListener("focus", flushPending);

    return () => {
      window.removeEventListener("online", flushPending);
      window.removeEventListener("focus", flushPending);
    };
  }, []);

  return (
    <>
      {SHELL_PAGES.map(({ path, Component }) => {
        const isActive = path === activePath;
        return (
          <div
            key={path}
            style={isActive ? undefined : { display: "none" }}
            aria-hidden={!isActive}
          >
            <Component />
          </div>
        );
      })}
      <AppNav />
    </>
  );
}

export default AppShell;
